import { useEffect, useState } from "react";
import { TagGroup } from "@models/tagGroup.model";
import { Tags } from "@models/tags.model";
import { TagInput } from "../../../components/common/tags/tagInput";
import { CreateServiceV3 } from "../../createV3/CreateService";
import Loader from "../../../components/common/loader/loader";

interface AdTagsPanelProps {
    adId: number;
    adName: string;
}

const createService = new CreateServiceV3();

export default function AdTagsPanel({ adId, adName: _adName }: AdTagsPanelProps) {
    const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
    const [tagsLinked, setTagsLinked] = useState<TagGroup[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function loadTags() {
            setLoading(true);
            try {
                const [tagGroupsData, linked] = await Promise.all([
                    createService.getTagGroups(),
                    createService.getTagsLinkedToTemplate(adId),
                ]);

                if (cancelled) return;

                setTagsLinked(linked ?? []);

                if (!tagGroupsData) {
                    setTagGroups([]);
                    return;
                }

                const groups: TagGroup[] = await Promise.all(
                    tagGroupsData.map(async (tg: TagGroup) => {
                        const tags = await createService.getTags(tg.id);
                        const tagGroup = new TagGroup();
                        tagGroup.id = Number(tg.id);
                        tagGroup.name = tg.name;
                        tagGroup.color = tg.color ?? null;
                        tagGroup.tags = (tags ?? []).map((tag: Tags) => {
                            const nextTag = new Tags();
                            nextTag.id = Number(tag.id);
                            nextTag.name = tag.name;
                            nextTag.tagGroupId = Number(tg.id);
                            nextTag.color = tag.color ?? null;
                            nextTag.tagGroup = null;
                            return nextTag;
                        });
                        return tagGroup;
                    }),
                );

                if (!cancelled) setTagGroups(groups);
            } catch (error) {
                console.error("Failed to load ad tags:", error);
                if (!cancelled) {
                    setTagGroups([]);
                    setTagsLinked([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        void loadTags();
        return () => { cancelled = true; };
    }, [adId]);

    const addTag = (tag: Tags, tagGroupId: number) => {
        setTagGroups((prev) =>
            prev.map((tg) =>
                tg.id === tagGroupId
                    ? Object.assign(new TagGroup(), tg, { tags: [...tg.tags, tag] })
                    : tg,
            ),
        );
    };

    return (
        <div className="ceh-tags-panel">
            {/*<div className="ceh-tags-panel-header">Tags</div>*/}
            <div className="ceh-tags-panel-section-title">Tags</div>
            {loading ? (
                <div className="ceh-tags-panel-loader"><Loader /></div>
            ) : tagGroups.length === 0 ? (
                <div className="ceh-tags-panel-empty">No tag groups found</div>
            ) : (
                <div className="ceh-tags-panel-groups">
                    {tagGroups.map((tagGroup) => (
                        <div key={tagGroup.id} className="ceh-tags-panel-group">
                            <span className="ceh-tags-panel-group-name">{tagGroup.name}</span>
                            <TagInput
                                templateId={adId}
                                tags={tagGroup.tags as any}
                                tagGroup={tagGroup as any}
                                tagsLinked={tagsLinked as any}
                                addTag={addTag}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
